"use strict";
/**
 * Évaluateur d'expressions sandboxé pour les `relevant` / `constraint` /
 * `calculation` des définitions d'enquête. Pas d'`eval`/`new Function` —
 * tokenizer + parseur récursif descendant + interprétation.
 *
 * Grammaire (récursive descente, précédence : ||, &&, !, comparaison,
 * +/-, *!/, primaire) :
 *
 *   expr     := orExpr
 *   orExpr   := andExpr ('||' andExpr)*
 *   andExpr  := notExpr ('&&' notExpr)*
 *   notExpr  := '!' notExpr | cmpExpr
 *   cmpExpr  := addExpr (('=='|'!='|'<'|'<='|'>'|'>='|'=') addExpr)?
 *   addExpr  := mulExpr (('+'|'-') mulExpr)*
 *   mulExpr  := primary (('*'|'/') primary)*
 *   primary  := number | string | '${' ident '}' | funcCall | '(' expr ')' | 'true' | 'false' | 'null'
 *   funcCall := ident '(' (expr (',' expr)*)? ')'
 *
 * Fonctions disponibles :
 *   - selected(multiSel: string[] | null, value: string): boolean
 *   - count_selected(multiSel: string[] | null): number   (alias: count-selected)
 *   - not(b: any): boolean
 *   - if(cond, a, b): a | b
 *   - length(s: string | string[] | null): number
 *
 * Voir app/docs/SURVEY_ENGINE_SPEC.md.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExpressionError = void 0;
exports.evaluate = evaluate;
exports.evaluateBool = evaluateBool;
exports.extractVariables = extractVariables;
class ExpressionError extends Error {
}
exports.ExpressionError = ExpressionError;
const KEYWORDS = {
    true: true,
    false: false,
    null: null,
};
/** Tokenize. Throws on unknown char. */
function tokenize(src) {
    const out = [];
    let i = 0;
    const len = src.length;
    while (i < len) {
        const c = src[i];
        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i++;
            continue;
        }
        // string
        if (c === '"' || c === "'") {
            const start = i;
            const quote = c;
            let s = '';
            i++;
            while (i < len && src[i] !== quote) {
                if (src[i] === '\\' && i + 1 < len) {
                    s += src[i + 1];
                    i += 2;
                }
                else {
                    s += src[i];
                    i++;
                }
            }
            if (i >= len)
                throw new ExpressionError(`Unterminated string at ${start}`);
            i++; // skip closing quote
            out.push({ kind: 'str', value: s, pos: start });
            continue;
        }
        // number
        if ((c >= '0' && c <= '9') || (c === '.' && i + 1 < len && src[i + 1] >= '0' && src[i + 1] <= '9')) {
            const start = i;
            let s = '';
            while (i < len && ((src[i] >= '0' && src[i] <= '9') || src[i] === '.')) {
                s += src[i];
                i++;
            }
            const n = parseFloat(s);
            if (!Number.isFinite(n))
                throw new ExpressionError(`Invalid number at ${start}: ${s}`);
            out.push({ kind: 'num', value: n, pos: start });
            continue;
        }
        // variable ${name}
        if (c === '$' && i + 1 < len && src[i + 1] === '{') {
            const start = i;
            i += 2;
            let name = '';
            while (i < len && src[i] !== '}') {
                name += src[i];
                i++;
            }
            if (i >= len)
                throw new ExpressionError(`Unterminated variable at ${start}`);
            i++; // skip }
            if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
                throw new ExpressionError(`Invalid variable name at ${start}: ${name}`);
            }
            out.push({ kind: 'var', value: name, pos: start });
            continue;
        }
        // identifier or function name (supports `-` and `_`)
        if ((c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_') {
            const start = i;
            let s = '';
            while (i < len && /[a-zA-Z0-9_-]/.test(src[i])) {
                s += src[i];
                i++;
            }
            out.push({ kind: 'ident', value: s, pos: start });
            continue;
        }
        // operators
        const two = src.slice(i, i + 2);
        if (two === '==' || two === '!=' || two === '<=' || two === '>=' || two === '&&' || two === '||') {
            out.push({ kind: 'op', value: two, pos: i });
            i += 2;
            continue;
        }
        if (c === '(') {
            out.push({ kind: 'lparen', value: '(', pos: i });
            i++;
            continue;
        }
        if (c === ')') {
            out.push({ kind: 'rparen', value: ')', pos: i });
            i++;
            continue;
        }
        if (c === ',') {
            out.push({ kind: 'comma', value: ',', pos: i });
            i++;
            continue;
        }
        if (c === '<' || c === '>' || c === '+' || c === '-' || c === '*' || c === '/' || c === '!' || c === '=') {
            out.push({ kind: 'op', value: c, pos: i });
            i++;
            continue;
        }
        throw new ExpressionError(`Unexpected character at ${i}: ${c}`);
    }
    out.push({ kind: 'eof', value: '', pos: src.length });
    return out;
}
class Parser {
    toks;
    p = 0;
    constructor(toks) {
        this.toks = toks;
    }
    peek() { return this.toks[this.p]; }
    eat(kind, value) {
        const t = this.peek();
        if (t.kind !== kind || (value !== undefined && t.value !== value)) {
            throw new ExpressionError(`Expected ${kind}${value ? ` "${value}"` : ''}, got ${t.kind} "${t.value}" at ${t.pos}`);
        }
        this.p++;
        return t;
    }
    parse() {
        const n = this.orExpr();
        if (this.peek().kind !== 'eof') {
            throw new ExpressionError(`Unexpected token at ${this.peek().pos}: ${this.peek().value}`);
        }
        return n;
    }
    orExpr() {
        let left = this.andExpr();
        while (this.peek().kind === 'op' && this.peek().value === '||') {
            this.p++;
            const right = this.andExpr();
            left = { kind: 'or', left, right };
        }
        return left;
    }
    andExpr() {
        let left = this.notExpr();
        while (this.peek().kind === 'op' && this.peek().value === '&&') {
            this.p++;
            const right = this.notExpr();
            left = { kind: 'and', left, right };
        }
        return left;
    }
    notExpr() {
        if (this.peek().kind === 'op' && this.peek().value === '!') {
            this.p++;
            return { kind: 'not', expr: this.notExpr() };
        }
        return this.cmpExpr();
    }
    cmpExpr() {
        const left = this.addExpr();
        const t = this.peek();
        if (t.kind === 'op') {
            const op = t.value;
            if (op === '==' || op === '!=' || op === '<' || op === '<=' || op === '>' || op === '>=' || op === '=') {
                this.p++;
                const right = this.addExpr();
                return { kind: 'cmp', op: op === '=' ? '==' : op, left, right };
            }
        }
        return left;
    }
    addExpr() {
        let left = this.mulExpr();
        while (this.peek().kind === 'op' && (this.peek().value === '+' || this.peek().value === '-')) {
            const op = this.peek().value;
            this.p++;
            const right = this.mulExpr();
            left = { kind: 'bin', op, left, right };
        }
        return left;
    }
    mulExpr() {
        let left = this.primary();
        while (this.peek().kind === 'op' && (this.peek().value === '*' || this.peek().value === '/')) {
            const op = this.peek().value;
            this.p++;
            const right = this.primary();
            left = { kind: 'bin', op, left, right };
        }
        return left;
    }
    primary() {
        const t = this.peek();
        if (t.kind === 'num') {
            this.p++;
            return { kind: 'lit', value: t.value };
        }
        if (t.kind === 'str') {
            this.p++;
            return { kind: 'lit', value: t.value };
        }
        if (t.kind === 'var') {
            this.p++;
            return { kind: 'var', name: t.value };
        }
        if (t.kind === 'lparen') {
            this.p++;
            const e = this.orExpr();
            this.eat('rparen');
            return e;
        }
        if (t.kind === 'ident') {
            const name = t.value;
            this.p++;
            if (this.peek().kind === 'lparen') {
                this.p++;
                const args = [];
                if (this.peek().kind !== 'rparen') {
                    args.push(this.orExpr());
                    while (this.peek().kind === 'comma') {
                        this.p++;
                        args.push(this.orExpr());
                    }
                }
                this.eat('rparen');
                return { kind: 'call', name, args };
            }
            // identifier as keyword (true / false / null)
            if (name in KEYWORDS)
                return { kind: 'lit', value: KEYWORDS[name] };
            throw new ExpressionError(`Unknown identifier "${name}" at ${t.pos}`);
        }
        throw new ExpressionError(`Unexpected token at ${t.pos}: ${t.kind} ${t.value}`);
    }
}
function toBool(v) {
    if (v === null || v === undefined)
        return false;
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'number')
        return v !== 0;
    if (typeof v === 'string')
        return v.length > 0;
    if (Array.isArray(v))
        return v.length > 0;
    return Object.keys(v).length > 0;
}
function eq(a, b) {
    if (a === b)
        return true;
    if (a == null || b == null)
        return a == null && b == null;
    if (typeof a === 'number' && typeof b === 'string')
        return String(a) === b;
    if (typeof a === 'string' && typeof b === 'number')
        return a === String(b);
    return false;
}
function num(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'string') {
        const n = parseFloat(v);
        if (!Number.isFinite(n))
            throw new ExpressionError(`Not a number: ${v}`);
        return n;
    }
    if (v == null)
        throw new ExpressionError('Null in arithmetic');
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    throw new ExpressionError(`Not a number: ${JSON.stringify(v)}`);
}
const FUNCS = {
    selected: (args) => {
        const arr = args[0];
        const val = args[1];
        if (!Array.isArray(arr))
            return false;
        return arr.includes(String(val));
    },
    'count-selected': (args) => {
        const arr = args[0];
        return Array.isArray(arr) ? arr.length : 0;
    },
    count_selected: (args) => {
        const arr = args[0];
        return Array.isArray(arr) ? arr.length : 0;
    },
    not: (args) => !toBool(args[0]),
    if: (args) => (toBool(args[0]) ? args[1] : args[2]),
    length: (args) => {
        const v = args[0];
        if (v == null)
            return 0;
        if (typeof v === 'string' || Array.isArray(v))
            return v.length;
        return 0;
    },
};
function evalNode(n, answers) {
    switch (n.kind) {
        case 'lit': return n.value;
        case 'var': {
            const v = answers[n.name];
            // null/undefined → null (distinguer plus tard côté caller si besoin)
            if (v === undefined)
                return null;
            return v;
        }
        case 'not': return !toBool(evalNode(n.expr, answers));
        case 'and': return toBool(evalNode(n.left, answers)) && toBool(evalNode(n.right, answers));
        case 'or': return toBool(evalNode(n.left, answers)) || toBool(evalNode(n.right, answers));
        case 'cmp': {
            const a = evalNode(n.left, answers);
            const b = evalNode(n.right, answers);
            switch (n.op) {
                case '==': return eq(a, b);
                case '!=': return !eq(a, b);
                case '<': return num(a) < num(b);
                case '<=': return num(a) <= num(b);
                case '>': return num(a) > num(b);
                case '>=': return num(a) >= num(b);
            }
            throw new ExpressionError(`Unknown cmp op`);
        }
        case 'bin': {
            const a = num(evalNode(n.left, answers));
            const b = num(evalNode(n.right, answers));
            switch (n.op) {
                case '+': return a + b;
                case '-': return a - b;
                case '*': return a * b;
                case '/':
                    if (b === 0)
                        throw new ExpressionError('Division by zero');
                    return a / b;
            }
            throw new ExpressionError(`Unknown bin op`);
        }
        case 'call': {
            const fn = FUNCS[n.name];
            if (!fn)
                throw new ExpressionError(`Unknown function: ${n.name}`);
            const args = n.args.map((a) => evalNode(a, answers));
            return fn(args);
        }
    }
}
const CACHE = new Map();
function parse(src) {
    const cached = CACHE.get(src);
    if (cached)
        return cached;
    const ast = new Parser(tokenize(src)).parse();
    CACHE.set(src, ast);
    return ast;
}
/** Évalue une expression dans le contexte des réponses. Toujours déterministe. */
function evaluate(src, answers) {
    return evalNode(parse(src), answers);
}
/** Évalue en booléen (truthy). */
function evaluateBool(src, answers) {
    return toBool(evaluate(src, answers));
}
/** Pour tests unitaires / debug : extrait les variables utilisées par une expression. */
function extractVariables(src) {
    const out = new Set();
    function walk(n) {
        switch (n.kind) {
            case 'var':
                out.add(n.name);
                return;
            case 'not':
                walk(n.expr);
                return;
            case 'and':
            case 'or':
            case 'cmp':
            case 'bin':
                walk(n.left);
                walk(n.right);
                return;
            case 'call':
                n.args.forEach(walk);
                return;
            case 'lit': return;
        }
    }
    walk(parse(src));
    return [...out];
}
//# sourceMappingURL=expr.js.map